import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import {
  Box,
  Center,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { DisplayAggregateQuestionDto } from '@keyops-cep/api-client';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { toPng } from 'html-to-image';

import { MaxDiffAnswerDataWithDemographic } from '../../../pages/EngagementDetails/types';
import i18n from '../../../utils/i18n';
import ColumnHeader from '../../TableComponents/ColumnHeader';
import { VisualizationChartRef } from '..';

import { buildAttributeTallies } from '.';

type Props = {
  question: DisplayAggregateQuestionDto;
  questionAnswers: MaxDiffAnswerDataWithDemographic[];
};

type Row = { attibute: string; best: number; worst: number };

const columnHelper = createColumnHelper<Row>();

const ChoiceDistributionMaxDiff = forwardRef<VisualizationChartRef, Props>(
  ({ question, questionAnswers }, ref) => {
    const tableRef = useRef<HTMLDivElement | null>(null);
    const [sorting, setSorting] = React.useState<SortingState>([
      { id: 'best', desc: true }, // sort "best" by default
    ]);

    const data: Row[] = useMemo(() => {
      const attributeLabels = (question.choices || []).map((c) => c.label);
      const { attributeTallies } = buildAttributeTallies(
        attributeLabels,
        questionAnswers
      );
      return attributeLabels.map((label) => ({
        attibute: label,
        best: attributeTallies[label]?.best ?? 0,
        worst: attributeTallies[label]?.worst ?? 0,
      }));
    }, [question, questionAnswers]);

    // Column max (for color scaling)
    const { maxBest, maxWorst } = useMemo(() => {
      const bests = data.map((r) => r.best);
      const worsts = data.map((r) => r.worst);
      return {
        maxBest: Math.max(1, ...bests),
        maxWorst: Math.max(1, ...worsts),
      };
    }, [data]);

    // Color bases (RGB)
    const greenRGB = useColorModeValue<
      [number, number, number],
      [number, number, number]
    >(
      [56, 161, 105], // green.500
      [72, 187, 120] // green.400
    );
    const redRGB = useColorModeValue<
      [number, number, number],
      [number, number, number]
    >(
      [245, 101, 101], // red.400
      [229, 62, 62] // red.500
    );

    const makeRGBA = (rgb: [number, number, number], a: number) =>
      `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})`;

    useImperativeHandle(ref, () => {
      const createImageFromTable = async (): Promise<string | null> => {
        if (!tableRef.current) return null;

        const clone = tableRef.current.cloneNode(true) as HTMLDivElement;
        const originalWidth = getComputedStyle(tableRef.current).width;
        clone.style.height = 'auto';
        clone.style.maxHeight = 'none';
        clone.style.overflow = 'hidden';
        clone.style.width = originalWidth;
        document.body.appendChild(clone);

        try {
          const dataUrl = await toPng(clone);
          return dataUrl;
        } catch (err) {
          console.error('Failed to create image from table', err);
          return null;
        } finally {
          document.body.removeChild(clone);
        }
      };

      return {
        onDownload: async (name: string) => {
          const dataUrl = await createImageFromTable();
          if (dataUrl) {
            const link = document.createElement('a');
            link.download = `${name}.png`;
            link.href = dataUrl;
            link.click();
          }
        },
        onCopyToClipboard: async () => {
          const dataUrl = await createImageFromTable();
          if (dataUrl) {
            const blob = await (await fetch(dataUrl)).blob();
            const item = new ClipboardItem({ 'image/png': blob });
            await navigator.clipboard.write([item]);
          }
        },
      };
    });

    const columns = useMemo(
      () => [
        // 1) "attibute" column
        columnHelper.accessor('attibute', {
          enableSorting: false,
          header: () => null,
          cell: (info) => info.getValue(),
        }),

        // 2) "best" column
        columnHelper.accessor('best', {
          header: ({ column }) => (
            <ColumnHeader headerText={'best'} sorted={column.getIsSorted()} />
          ),
          cell: (info) => {
            const value = info.getValue() ?? 0;
            // green heatmap background
            const t = Math.min(1, Math.max(0, value / maxBest)); // 0 to 1
            const alpha = 0.12 + 0.64 * t;
            const bg = makeRGBA(greenRGB, alpha);
            return (
              <Box
                px={2}
                py={1}
                borderRadius="sm"
                fontSize="md"
                bg={bg}
                textAlign="center"
                fontWeight="bold"
              >
                {value}
              </Box>
            );
          },
          sortingFn: 'basic',
        }),

        // 3) "worst" column
        columnHelper.accessor('worst', {
          header: ({ column }) => (
            <ColumnHeader headerText={'worst'} sorted={column.getIsSorted()} />
          ),
          cell: (info) => {
            const value = info.getValue() ?? 0;
            // red heatmap background
            const t = Math.min(1, Math.max(0, value / maxWorst)); // 0 to 1
            const alpha = 0.12 + 0.64 * t;
            const bg = makeRGBA(redRGB, alpha);
            return (
              <Box
                px={2}
                py={1}
                borderRadius="sm"
                fontSize="md"
                bg={bg}
                textAlign="center"
                fontWeight="bold"
              >
                {value}
              </Box>
            );
          },
          sortingFn: 'basic',
        }),
      ],
      [maxBest, maxWorst, greenRGB, redRGB]
    );

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      onSortingChange: setSorting,
      getSortedRowModel: getSortedRowModel(),
      state: { sorting },
    });

    const tableBg = useColorModeValue('white', 'gray.700');
    const tableText = useColorModeValue('gray.700', 'white');
    const rowHoverBg = useColorModeValue('gray.200', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');

    return (
      <TableContainer
        ref={tableRef}
        bg={tableBg}
        borderWidth="thin"
        borderColor={borderColor}
        borderRadius="lg"
        maxH="100%"
        overflowY="auto"
      >
        <Table variant="simple" size="sm">
          <Thead position="sticky" top={0} bg={tableBg}>
            {table.getHeaderGroups().map((hg) => (
              <Tr key={hg.id}>
                {hg.headers.map((header) => (
                  <Th
                    key={header.id}
                    cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                    fontSize="sm"
                    onClick={header.column.getToggleSortingHandler()}
                    textTransform="none"
                    color={tableText}
                    textAlign="center"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: rowHoverBg }}>
                  {row.getVisibleCells().map((cell) => (
                    <Td key={cell.id} color={tableText} whiteSpace="pre-wrap">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" py={20}>
                  <Center>{i18n.t('visualization.noData')}</Center>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    );
  }
);

ChoiceDistributionMaxDiff.displayName = 'ChoiceDistributionMaxDiff';
export default ChoiceDistributionMaxDiff;
